import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import MarketHeader from "@/components/MarketHeader";
import { fmtCurrency, fmtNum, pnlClass } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Activity, Zap, TrendingUp, TrendingDown, Timer, IndianRupee } from "lucide-react";

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [trades, setTrades] = useState([]);

  useEffect(() => {
    const load = () => {
      api.get("/analytics/summary").then((r) => setSummary(r.data)).catch(() => {});
      api.get("/trades/today").then((r) => setTrades(r.data)).catch(() => {});
    };
    load();
    const t = setInterval(load, 1000);
    return () => clearInterval(t);
  }, []);

  const Stat = ({ label, value, valueClass = "", icon: Icon, testid }) => (
    <Card className="border-white/10 bg-[#141414] rounded-sm p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-slate-500" />}
      </div>
      <div className={`font-mono text-lg sm:text-2xl font-bold ${valueClass}`}>{value}</div>
    </Card>
  );

  const dailyTotalPnl = summary?.daily_total_pnl ?? summary?.today_pnl;
  const dailyRealizedPnl = summary?.realized_pnl ?? trades
    .filter((trade) => trade.status === "COMPLETED")
    .reduce((total, trade) => total + Number(trade.total_pnl || 0), 0);

  return (
    <div className="flex flex-col min-h-screen" data-testid="dashboard-page">
      <MarketHeader />
      <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
        <div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-slate-400 mt-0.5">Live overview of your automated options trading.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                  <Stat label="Daily Total P&L" value={fmtCurrency(dailyTotalPnl)} valueClass={pnlClass(dailyTotalPnl)}
                icon={IndianRupee} testid="stat-today-pnl" />
              <Stat label="Open MTM" value={fmtCurrency(summary?.running_pnl)} valueClass={pnlClass(summary?.running_pnl)}
                icon={Activity} testid="stat-running-pnl" />
                  <Stat label="Daily Realized P&L" value={fmtCurrency(dailyRealizedPnl)} valueClass={pnlClass(dailyRealizedPnl)}
                icon={TrendingUp} testid="stat-realized-pnl" />
          <Stat label="Active Strategies" value={fmtNum(summary?.active_strategies, 0)} icon={Zap} testid="stat-active-strategies" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
          <Stat label="Running Trades" value={fmtNum(summary?.running_trades, 0)} icon={Timer} testid="stat-running-trades" />
          <Stat label="Today Trades" value={fmtNum(summary?.today_trades, 0)} icon={Activity} testid="stat-today-trades" />
          <Stat label="Wins" value={fmtNum(summary?.wins, 0)} valueClass="text-emerald-400" icon={TrendingUp} testid="stat-wins" />
          <Stat label="Losses" value={fmtNum(summary?.losses, 0)} valueClass="text-rose-400" icon={TrendingDown} testid="stat-losses" />
        </div>

        <Card className="border-white/10 bg-[#141414] rounded-sm">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <h3 className="font-heading font-semibold text-base">Today's Trades</h3>
              <p className="text-xs text-slate-500 mt-0.5">Live and completed trades placed today.</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="today-trades-table">
              <thead className="bg-white/5 text-slate-500 uppercase tracking-widest text-[10px]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Strategy</th>
                  <th className="text-left px-3 py-2 font-medium">Index</th>
                  <th className="text-left px-3 py-2 font-medium">Mode</th>
                  <th className="text-right px-3 py-2 font-medium">Spot@Entry</th>
                  <th className="text-left px-3 py-2 font-medium">Legs</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-right px-3 py-2 font-medium">P&L</th>
                </tr>
              </thead>
              <tbody>
                {trades.length === 0 && (
                  <tr><td colSpan="7" className="text-center py-8 text-slate-500" data-testid="no-trades-msg">No trades today. Enable a strategy to get started.</td></tr>
                )}
                {trades.map((t) => (
                  <tr key={t.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`trade-row-${t.id}`}>
                    <td className="px-3 py-2 font-medium">{t.strategy_name}</td>
                    <td className="px-3 py-2 text-slate-400">{t.index}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-widest ${t.trade_mode === "live" ? "bg-rose-500/15 text-rose-400" : "bg-amber-500/15 text-amber-400"}`}>{t.trade_mode}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmtNum(t.spot_at_entry)}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {t.legs.map((l, i) => (
                          <span key={i} className={`px-1.5 py-0.5 rounded-sm text-[10px] font-mono ${l.status === "OPEN" ? "bg-blue-500/15 text-blue-300" : "bg-white/5 text-slate-400"}`}>
                            {l.side} {l.kind} {l.strike} @ {fmtNum(l.entry_price)}{l.status === "CLOSED" ? ` → ${fmtNum(l.exit_price)}` : ""} {l.sl_moved_to_be ? "•BE" : ""}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-widest ${
                        t.status === "RUNNING" ? "bg-emerald-500/15 text-emerald-400" :
                        t.status === "COMPLETED" ? "bg-slate-500/15 text-slate-400" : "bg-rose-500/15 text-rose-400"}`}>
                        {t.status}
                      </span>
                    </td>
                    <td className={`px-3 py-2 text-right font-mono font-semibold ${pnlClass(t.total_pnl)}`}>{fmtCurrency(t.total_pnl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

